import type { RequestPayload } from "@inertiajs/core"
import { Head } from "@inertiajs/react"
import { router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import ReportForm from "./_form"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Option {
  id: number
  name: string
}

interface ReportData {
  report_type: number
  category: number
  report_date: string
  notes: string
  admin_notes: string
  significant: number
  vendor_id: number | null
  investigator_name: string
}

export interface InitialEntryData {
  start_time: string
  entry_text: string
  evidence_type_ids: number[]
}

export interface EvidenceTypeOption {
  id: number
  name: string
  media_kind: string | null
  sort_order: number
}

interface Props {
  case_record: CaseRecord
  report: ReportData
  users: Option[]
  vendors: Option[]
  team_members: Option[]
  evidence_types: EvidenceTypeOption[]
  errors?: Record<string, string[]>
}

export default function New({
  case_record,
  report,
  users,
  vendors,
  team_members,
  evidence_types,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
    { title: "New", href: `/cases/${case_record.id}/reports/new` },
  ]

  const [data, setDataState] = useState<ReportData>({
    report_type: report.report_type,
    category: report.category,
    report_date: report.report_date,
    notes: report.notes,
    admin_notes: report.admin_notes,
    significant: report.significant,
    vendor_id: report.vendor_id,
    investigator_name: report.investigator_name,
  })

  const [teamMemberIds, setTeamMemberIds] = useState<number[]>([])
  const [teamLeaderId, setTeamLeaderId] = useState<number | null>(null)
  const [initialEntry, setInitialEntry] = useState<InitialEntryData>({
    start_time: "",
    entry_text: "",
    evidence_type_ids: [],
  })
  const [processing, setProcessing] = useState(false)

  const setData = (key: keyof ReportData, value: string | number | null) => {
    setDataState((prev) => ({ ...prev, [key]: value }))
  }

  const handleTeamMemberToggle = (id: number) => {
    setTeamMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const handleInitialEntryChange = (
    key: keyof InitialEntryData,
    value: InitialEntryData[keyof InitialEntryData],
  ) => {
    setInitialEntry((prev) => ({ ...prev, [key]: value }) as InitialEntryData)
  }

  const handleEvidenceTypeToggle = (id: number) => {
    setInitialEntry((prev) => ({
      ...prev,
      evidence_type_ids: prev.evidence_type_ids.includes(id)
        ? prev.evidence_type_ids.filter((x) => x !== id)
        : [...prev.evidence_type_ids, id],
    }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post(
      `/cases/${case_record.id}/reports`,
      {
        case_report: data,
        team_member_ids: teamMemberIds,
        team_leader_id: teamLeaderId,
        initial_entry: initialEntry,
      } as unknown as RequestPayload,
      {
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`New Report - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">New Report</h1>
        <ReportForm
          caseId={case_record.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Create Report"
          users={users}
          vendors={vendors}
          isNew
          teamMembers={team_members}
          teamMemberIds={teamMemberIds}
          onTeamMemberToggle={handleTeamMemberToggle}
          initialEntry={initialEntry}
          onInitialEntryChange={handleInitialEntryChange}
          evidenceTypes={evidence_types}
          onEvidenceTypeToggle={handleEvidenceTypeToggle}
          setTeamLeaderId={setTeamLeaderId}
        />
      </div>
    </AppLayout>
  )
}
